// Session transport: WebTransport (QUIC) when the browser has it, WebSocket
// otherwise. Both carry the same frames; WebTransport additionally gets
// unreliable datagrams for movement and a unidirectional stream per asset.

import { FRAME_HEADER_LEN, MAX_FRAME_BODY, Reader, Writer } from "./codec";
import {
  Channel,
  decodeMessage,
  encodeMessage,
  messageChannel,
  MESSAGE_IDS,
  type Endpoint,
  type Message,
} from "./messages";

export type SessionEvent =
  | { kind: "message"; message: Message }
  | { kind: "closed"; reason: string };

export interface SessionStats {
  bytesIn: number;
  bytesOut: number;
  messagesIn: number;
  messagesOut: number;
  transport: "webtransport" | "websocket";
}

/** Splits a byte stream into frames and decodes them. */
class FrameParser {
  private pending: Uint8Array | null = null;

  push(chunk: Uint8Array, out: (m: Message) => void): void {
    let buf = chunk;
    if (this.pending) {
      const joined = new Uint8Array(this.pending.length + chunk.length);
      joined.set(this.pending);
      joined.set(chunk, this.pending.length);
      buf = joined;
      this.pending = null;
    }
    let pos = 0;
    const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    while (buf.length - pos >= FRAME_HEADER_LEN) {
      const id = view.getUint16(pos, true);
      const len = view.getUint32(pos + 2, true);
      if (len > MAX_FRAME_BODY) throw new Error(`frame too large: ${len}`);
      const end = pos + FRAME_HEADER_LEN + len;
      if (buf.length < end) break;
      const r = new Reader(buf, pos + FRAME_HEADER_LEN, end);
      const message = decodeMessage(id, r);
      if (message && r.ok) out(message);
      pos = end;
    }
    if (pos < buf.length) this.pending = buf.slice(pos);
  }
}

export function encodeFrame(w: Writer, message: Message): void {
  w.u16(MESSAGE_IDS[message.type]);
  const slot = w.reserve(4);
  const start = w.length;
  encodeMessage(w, message);
  w.patchU32(slot, w.length - start);
}

export function frameBytes(message: Message): Uint8Array {
  const w = new Writer(128);
  encodeFrame(w, message);
  return w.finish();
}

export function parseFrames(buf: Uint8Array): Message[] {
  const out: Message[] = [];
  new FrameParser().push(buf, (m) => out.push(m));
  return out;
}

export function webTransportSupported(): boolean {
  return typeof (globalThis as { WebTransport?: unknown }).WebTransport === "function";
}

/**
 * How long, ms, a WebTransport session goes on being read once it can no
 * longer send, and once its connection has closed, for whatever the far
 * side said last and is still to be read.
 *
 * A region lets an avatar go on a teleport by telling it where it has
 * gone and closing the connection. A message the viewer sent as that
 * happened failed, and the failure was taken for the end of the session
 * there and then: everything still to be read was dropped, the word of
 * where the avatar had gone with it, and the viewer, thinking the
 * connection lost, reconnected to the region it had left.
 */
export const LAST_WORD_MS = 3000;
/** How long, ms, a session whose control stream has ended waits for its connection to say why. */
export const REASON_WAIT_MS = 500;

/**
 * Why a WebTransport session has ended, once it has: when its connection
 * has closed and its control stream has been read to the end, whichever
 * comes first waiting a while for the other. `heard` settles with null as
 * the control stream ends, or with why reading it failed; `closed` with
 * why the connection closed, which is the reason given when it comes.
 */
export async function lastWord(
  heard: Promise<string | null>,
  closed: Promise<string>,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): Promise<string> {
  const first = await Promise.race([
    heard.then((error) => ({ heard: true as const, error })),
    closed.then((reason) => ({ heard: false as const, reason })),
  ]);
  if (!first.heard) {
    await Promise.race([heard, sleep(LAST_WORD_MS)]);
    return first.reason;
  }
  const reason = await Promise.race([closed, sleep(REASON_WAIT_MS).then(() => null)]);
  return reason ?? first.error ?? "connection closed";
}

/**
 * Milliseconds of pretend distance, each way, from `starfall.latency`.
 *
 * Crossing a region border, handing over between two sessions, arriving
 * somewhere and finding it already drawn: all of it behaves differently
 * when the wire is slow, and localhost is never slow. Setting this makes
 * a fast connection act like a far one so those paths can be walked
 * through rather than reasoned about.
 *
 *     starfall.latency = 250     // a quarter second each way
 *     starfall.latency = 0       // back to the truth
 */
function pretendLatency(): number {
  const dev = (window as unknown as { starfall?: { latency?: number } }).starfall;
  const ms = dev?.latency ?? 0;
  return Number.isFinite(ms) && ms > 0 ? Math.min(ms, 5000) : 0;
}

/**
 * One connection to a grid or region service. Messages arrive through
 * `onEvent`; `send` picks the channel from the message's declared one.
 */
export class Session {
  readonly stats: SessionStats;
  private closed = false;
  private closeReason = "";
  private wt: WebTransport | null = null;
  private ws: WebSocket | null = null;
  private control: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private datagrams: WritableStreamDefaultWriter<Uint8Array> | null = null;
  private outbox: Uint8Array[] = [];
  private flushing = false;
  private listeners = new Set<(e: SessionEvent) => void>();
  /**
   * Why sending failed, once it has: nothing more is sent, but what the far
   * side says is still read until the session ends, or LAST_WORD_MS on.
   */
  private sendFailure: string | null = null;
  private givingUp: ReturnType<typeof setTimeout> | null = null;

  private constructor(transport: SessionStats["transport"]) {
    this.stats = { bytesIn: 0, bytesOut: 0, messagesIn: 0, messagesOut: 0, transport };
  }

  onEvent(fn: (e: SessionEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  get isClosed(): boolean {
    return this.closed;
  }

  /**
   * Open a session to `endpoint`. WebTransport is tried first; the endpoint's
   * WebSocket URL is the fallback when the browser lacks it or QUIC is blocked.
   */
  static async connect(endpoint: Endpoint, path: string, preferWebSocket = false): Promise<Session> {
    if (webTransportSupported() && !preferWebSocket) {
      try {
        return await Session.connectWebTransport(endpoint, path);
      } catch (e) {
        if (!endpoint.websocket_url) throw e;
        console.warn("WebTransport failed, falling back to WebSocket:", e);
      }
    }
    if (!endpoint.websocket_url) throw new Error("this browser has no WebTransport and the server offers no WebSocket fallback");
    return Session.connectWebSocket(endpoint.websocket_url);
  }

  static async connectWebTransport(endpoint: Endpoint, path: string): Promise<Session> {
    const url = `https://${endpoint.host}:${endpoint.port}${path}`;
    const options: WebTransportOptions = {};
    if (endpoint.certificate_hash) {
      options.serverCertificateHashes = [{ algorithm: "sha-256", value: base64ToBytes(endpoint.certificate_hash) }];
    }
    const wt = new WebTransport(url, options);
    await wt.ready;
    const session = new Session("webtransport");
    session.wt = wt;
    const control = await wt.createBidirectionalStream();
    session.control = control.writable.getWriter();
    session.datagrams = wt.datagrams.writable.getWriter();
    const heard = session.pumpStream(control.readable);
    void session.acceptUniStreams(wt);
    void session.pumpDatagrams(wt);
    const closed = wt.closed.then(
      (info) => `closed (${info.closeCode ?? 0}) ${info.reason ?? ""}`.trim(),
      (e) => `connection lost: ${String(e)}`,
    );
    // Over once both have ended, so that nothing said before the close is
    // dropped unread; and let go of, should the stream alone have failed.
    void lastWord(heard, closed).then((reason) => session.close(0, reason));
    return session;
  }

  static connectWebSocket(url: string): Promise<Session> {
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";
      const session = new Session("websocket");
      session.ws = ws;
      const parser = new FrameParser();
      ws.onopen = () => resolve(session);
      ws.onerror = () => {
        if (!session.closed) reject(new Error(`WebSocket to ${url} failed`));
      };
      ws.onclose = (ev) => {
        session.finish(`closed (${ev.code}) ${ev.reason}`.trim());
        reject(new Error("WebSocket closed"));
      };
      ws.onmessage = (ev) => {
        const bytes = new Uint8Array(ev.data as ArrayBuffer);
        session.stats.bytesIn += bytes.length;
        try {
          parser.push(bytes, (m) => session.deliver(m));
        } catch (e) {
          session.close(4, `protocol error: ${String(e)}`);
        }
      };
    });
  }

  send(message: Message): void {
    if (this.closed) return;
    const wait = pretendLatency();
    if (wait > 0) {
      // Held, not dropped, and in the order it was written: a slow wire,
      // not a lossy one.
      const held = message;
      setTimeout(() => this.write(held), wait);
      return;
    }
    this.write(message);
  }

  private write(message: Message): void {
    if (this.closed || this.sendFailure !== null) return;
    const bytes = frameBytes(message);
    this.stats.messagesOut++;
    this.stats.bytesOut += bytes.length;
    const channel = messageChannel(MESSAGE_IDS[message.type]) ?? Channel.Reliable;
    if (this.ws) {
      if (this.ws.readyState === WebSocket.OPEN) this.ws.send(bytes);
      return;
    }
    if (channel === Channel.Unreliable && this.datagrams && bytes.length <= 1150) {
      this.datagrams.write(bytes).catch(() => {});
      return;
    }
    this.outbox.push(bytes);
    void this.flush();
  }

  close(code = 0, reason = "bye"): void {
    if (this.closed) return;
    this.finish(reason);
    try {
      this.wt?.close({ closeCode: code, reason });
    } catch {
      // already gone
    }
    try {
      this.ws?.close(4000 + code, reason);
    } catch {
      // already gone
    }
  }

  private async flush(): Promise<void> {
    if (this.flushing || !this.control) return;
    this.flushing = true;
    try {
      while (this.outbox.length > 0 && !this.closed) {
        const batch = this.outbox;
        this.outbox = [];
        const total = batch.reduce((n, b) => n + b.length, 0);
        const joined = new Uint8Array(total);
        let at = 0;
        for (const b of batch) {
          joined.set(b, at);
          at += b.length;
        }
        await this.control.write(joined);
      }
    } catch (e) {
      this.sendFailed(`send failed: ${String(e)}`);
    } finally {
      this.flushing = false;
    }
  }

  /**
   * Sending has failed: nothing more is sent, and the session ends when it
   * has been read to the end and its connection has closed (lastWord), or
   * LAST_WORD_MS on if neither happens. The far side may well have more to
   * say: a region closing on a teleport has its last word, where the avatar
   * went, still to be read as what the viewer sends begins to fail.
   */
  private sendFailed(reason: string): void {
    if (this.closed || this.sendFailure !== null) return;
    this.sendFailure = reason;
    this.outbox = [];
    this.givingUp = setTimeout(() => this.close(0, reason), LAST_WORD_MS);
  }

  /**
   * Read a stream's messages to its end: null once it has ended, or why it
   * could not be read further. A failed stream ends nothing by itself; the
   * control stream's end is one of the two the session waits for
   * (lastWord), and a picture's stream ends with the picture.
   */
  private async pumpStream(readable: ReadableStream<Uint8Array>): Promise<string | null> {
    const reader = readable.getReader();
    const parser = new FrameParser();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done || !value) return null;
        this.stats.bytesIn += value.length;
        parser.push(value, (m) => this.deliver(m));
      }
    } catch (e) {
      return `stream error: ${String(e)}`;
    }
  }

  private async acceptUniStreams(wt: WebTransport): Promise<void> {
    const reader = wt.incomingUnidirectionalStreams.getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done || !value) break;
        void this.pumpStream(value);
      }
    } catch {
      // connection closing
    }
  }

  private async pumpDatagrams(wt: WebTransport): Promise<void> {
    const reader = wt.datagrams.readable.getReader();
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done || !value) break;
        this.stats.bytesIn += value.length;
        for (const m of parseFrames(value)) this.deliver(m);
      }
    } catch {
      // connection closing
    }
  }

  private deliver(message: Message): void {
    this.stats.messagesIn++;
    const wait = pretendLatency();
    if (wait > 0) {
      setTimeout(() => {
        if (this.closed) return;
        for (const fn of this.listeners) fn({ kind: "message", message });
      }, wait);
      return;
    }
    for (const fn of this.listeners) fn({ kind: "message", message });
  }

  private finish(reason: string): void {
    if (this.closed) return;
    this.closed = true;
    this.closeReason = reason;
    if (this.givingUp !== null) clearTimeout(this.givingUp);
    this.givingUp = null;
    for (const fn of this.listeners) fn({ kind: "closed", reason });
    this.listeners.clear();
  }

  get reason(): string {
    return this.closeReason;
  }

  /** Resolve with the first message matching `pick`, or reject on close/timeout. */
  waitFor<T>(pick: (m: Message) => T | undefined, timeoutMs = 15000): Promise<T> {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        off();
        reject(new Error("timed out waiting for the server"));
      }, timeoutMs);
      const off = this.onEvent((e) => {
        if (e.kind === "closed") {
          clearTimeout(timer);
          off();
          reject(new Error(e.reason || "connection closed"));
          return;
        }
        const v = pick(e.message);
        if (v !== undefined) {
          clearTimeout(timer);
          off();
          resolve(v);
        }
      });
    });
  }
}

export function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
